<template>
    <div>
      <v-row>
        <v-col>
          <div class="d-flex justify-space-between mt-6 mb-3">
            <h3 class="text-text-primary">{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.yourOffer') }}</h3>
            <app-button
              v-if="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer"
              type="outlined"
              variant="neutral"
              @click="$emit('changeOffer')"
            >
              {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.changeOffer') }}
            </app-button>
          </div>

          <div class="heading-xs-bold text-text-primary">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomSizeText') }}
          </div>
          <v-chip-group v-model="selectedData"
                        data-locator="size-chip-group"
                        selected-class="offer-choice-chip--selected"
                        mandatory
                        @update:model-value="onDataRoomSettingsChange"
          >
            <v-chip v-for="item in data"
                    :key="`data-${item.value}`"
                    class="offer-choice-chip"
                    variant="flat"
                    :ripple="false"
                    :value="item"
                    :disabled="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer && !areSameSizeData(item, selectedData)"
            >
              {{ item.text }}
            </v-chip>
            <v-chip
              class="customDataChip offer-choice-chip"
              variant="flat"
              :ripple="false"
              value="additional"
              :disabled="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer"
            >
              <v-select v-model="selectedAdditionalData"
                        :items="additionalData"
                        :placeholder="$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.other')"
                        class="customDataSelect mt-0 closd-select"
                        :menu-props="{ contentClass: 'closd-select__menu' }"
                        hide-details
                        item-title="text"
                        return-object
                        rounded
                        @click.stop
                        @mousedown.stop
                        @update:model-value="onSelectedAdditionalDataChange"
              />
            </v-chip>
          </v-chip-group>
          <div v-if="hasAddonData"
               class="detail-md-regular text-text-tertiary mt-1"
          >
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.priceForAdditionalData', { size: computedAddonStorage, price: $n(computedAddonStoragePrice, 'decimal') }) }}
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-5">
        <v-col>
          <div class="heading-xs-bold text-text-primary">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomDurationText') }}
          </div>
          <div>
            <v-chip-group v-model="selectedDuration"
                          data-locator="duration-chip-group"
                          selected-class="offer-choice-chip--selected"
                          mandatory
                          @update:model-value="onDataRoomSettingsChange"
            >
              <v-chip v-for="item in durations"
                      :key="`duration-${item.value}`"
                      class="offer-choice-chip"
                      variant="flat"
                      :ripple="false"
                      :value="item"
                      :disabled="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer && item !== selectedDuration"
              >
                {{ item.text }}
              </v-chip>
            </v-chip-group>
          </div>
          <div v-if="dataRoomCustomPricing && dataRoomCustomPricing.discountPercent"
               class="detail-md-regular text-text-tertiary"
          >
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discount', { discount: dataRoomCustomPricing.discountPercent }) }}
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-6">
        <v-col>
          <h3 class="text-text-primary">{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.offerDetails') }}</h3>

          <div class="offer-details-container rounded-lg pa-5 mt-3" data-locator="pricing-container">
            <div class="d-flex flex-column">
              <div class="d-flex justify-space-between">
                <div>
                  <i18n-t
                    :plural="newDataRoomDuration.value"
                    keypath="projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomForSizeAndDuration"
                    scope="global"
                  >
                    <template #size>
                      <span>
                        {{ `${dataRoomCustomPricing.size} ${$t(`projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.${dataRoomCustomPricing.unit ?? 'Mb'}`)}` }}
                      </span>
                    </template>
                    <template #duration>
                      <span>
                        {{ `${dataRoomCustomPricing.months} ${$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.months', dataRoomCustomPricing.months)}` }}
                      </span>
                    </template>
                  </i18n-t>
                </div>

                <span data-locator="base-monthly-cost">
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.netAmountPerMonth', { amount: $n(dataRoomCustomPricing.monthlyCost, 'decimal') }) }}
                </span>
              </div>

              <div
                v-if="dataRoomCustomPricing.customerDiscountPercent > 0"
                class="d-flex justify-space-between text-text-info mt-1"
                data-locator="customer-discount-line"
              >
                <span>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.customerDiscount', { discount: dataRoomCustomPricing.customerDiscountPercent }) }}
                </span>
                <span>
                   {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discountNetAmountPerMonth', { amount: $n(dataRoomCustomPricing.customerDiscountAmount, 'decimal') }) }}
                </span>
              </div>

              <div
                v-if="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer"
                class="d-flex justify-space-between text-text-info mt-1"
                data-locator="special-offer-line"
              >
                <span>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.customerDiscountDataroomSpecialOffer', { discount: dataRoomCustomPricing.customerDiscountDataroomSpecialOfferPercent }) }}
                </span>
                <span>
                   {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discountNetAmountPerMonth', { amount: $n(dataRoomCustomPricing.customerDiscountDataroomSpecialOfferAmount, 'decimal') }) }}
                </span>
              </div>

              <v-divider class="my-5 border-opacity-100" color="border-primary" />

              <div class="d-flex justify-space-between">
                <span class="body-lg-bold text-text-primary">
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.totalAmount') }}
                </span>
                <span class="body-lg-bold text-text-primary" data-locator="final-monthly-cost">
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.netAmountPerMonth', { amount: $n(dataRoomCustomPricing.monthlyCostWithDiscount, 'decimal') }) }}
                </span>
              </div>

              <p class="mt-6">
                <i18n-t
                  :plural="newDataRoomDuration.value"
                  class="mt-2"
                  keypath="projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.commitment"
                  scope="global"
                >
                  <template #duration>
                  <span>
                    <span v-t="{
                              path: 'projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount',
                              plural: newDataRoomDuration.value,
                              args: { months: newDataRoomDuration.value },
                            }"
                    />
                  </span>
                  </template>
                  <template #price>
                  <span>
                    {{ $t('common.price.excludingVAT', { amount: $n(dataRoomCustomPricing.monthlyCostWithDiscount, 'decimal') }) }}
                  </span>
                  </template>
                </i18n-t>
              </p>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-6 mb-1">
        <v-col>
          <div>
            <v-checkbox v-model="validator.$model"
                        :error-messages="termsCheckedError"
                        class="mt-0 pt-0"
                        :hide-details="termsCheckedError.length === 0"
            >
              <template #label>
                <span>{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text1') }}
                  <a href="#" @click.stop>
                    <AppDownloadFile :link="dataroomTermsLink">
                      <template #default="{ submitForm }">
                        <span
                          class="text-darkblue-500 app-link text-decoration-underline"
                          @click="submitForm"
                        >
                          {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text2') }}
                        </span>
                      </template>
                    </AppDownloadFile>
                  </a>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text3') }}
                </span>
              </template>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapActions, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { PPU_DATAROOM_ADDITIONAL_SIZE_OPTIONS, PPU_DATAROOM_BASE_SIZE_OPTIONS } from '@/common/utils/datarooms.ts'
import { formFieldErrors } from '@/common/utils/form'
import { GET_DATA_ROOM_CUSTOM_PRICING } from '@/store/modules/rooms/action_types'

const ADDITIONAL_DATA_VALUE = 'additional'

export default {
  name: 'ProjectsNewDataroomDialogSetupDataRoom',
  components: { AppDownloadFile },
  props: {
    newDataRoom: {
      type: Object,
      required: true,
    },
    validator: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      selectedDuration: null,
      selectedData: null,
      selectedAdditionalData: null,
      durations: [
        { value: 1, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 1 }, 1) },
        { value: 3, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 3 }, 3) },
        { value: 6, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 6 }, 6) },
        { value: 12, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 12 }, 12) },
      ],
      data: PPU_DATAROOM_BASE_SIZE_OPTIONS.map(this.getSizeOptionWithLabelText),
      additionalData: PPU_DATAROOM_ADDITIONAL_SIZE_OPTIONS.map(this.getSizeOptionWithLabelText),
    }
  },
  computed: {
    ...mapState('rooms', ['dataRoomCustomPricing', 'dataRoomCustomPricingPending']),
    newDataRoomData: {
      get () {
        return {
          value: this.newDataRoom.storage,
          unit: this.newDataRoom.unit,
        }
      },
      set (data) {
        this.newDataRoom.storage = data.value
        this.newDataRoom.unit = data.unit
      },
    },
    newDataRoomDuration: {
      get () {
        return {
          value: this.newDataRoom.duration,
        }
      },
      set (duration) {
        this.newDataRoom.duration = duration.value
      },
    },
    computedAddonStorage () {
      if (this.hasAddonData) {
        const size = this.dataRoomCustomPricing.additionalStorageSize
        if (size < 1) {
          return `${size * 1000} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Mb')}`
        } else {
          return `${size} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}`
        }
      }
      return null
    },
    computedAddonStoragePrice () {
      return this.dataRoomCustomPricing.customerDiscountPercent
        ? this.dataRoomCustomPricing.additionalStoragePriceWithDiscount
        : this.dataRoomCustomPricing.additionalStoragePrice
    },
    hasAddonData () {
      if (this.dataRoomCustomPricing) {
        return !!(this.dataRoomCustomPricing.additionalStorageSize && this.dataRoomCustomPricing.additionalStoragePrice)
      }
      return null
    },
    termsCheckedError () {
      return formFieldErrors(
        this.validator,
        [
          ['sameAs', this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.termsCheckedValidationError')],
        ],
      )
    },
    dataroomTermsLink () {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=dataroom-price-list-${this.$i18n.locale.toUpperCase()}`
    },
  },
  created () {
    // In V2 we had a mandatory prop on chip-groups that auto-selected their first value
    // In V3 we need to do it manually because the prop doesn't exist anymore
    this.selectedDuration = this.durations[0]
    this.selectedData = this.data[0]
    this.onDataRoomSettingsChange()
  },
  methods: {
    ...mapActions('rooms', [GET_DATA_ROOM_CUSTOM_PRICING]),
    getSizeOptionWithLabelText(sizeOption) {
      return {
        ...sizeOption,
        text: `${sizeOption.value} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.' + sizeOption.unit)}`,
      }
    },
    async onDataRoomSettingsChange () {
      let actualSelectedData = this.selectedData === ADDITIONAL_DATA_VALUE ? this.selectedAdditionalData : this.selectedData

      if (this.selectedDuration?.value && actualSelectedData.value && actualSelectedData.unit) {
        try {
          await this.GET_DATA_ROOM_CUSTOM_PRICING({
            duration: this.selectedDuration.value,
            unit: actualSelectedData.unit,
            storage: actualSelectedData.value,
          })

          if (this.dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer) {
            this.forceSelectedDurationFromSpecialOffer()
            this.forceSelectedDataFromSpecialOffer()
            actualSelectedData = this.selectedData
          }

          this.newDataRoomDuration = cloneDeep(this.selectedDuration)
          this.newDataRoomData = cloneDeep(actualSelectedData)
        } catch (e) {
          console.error(e)
        }
      }
    },
    forceSelectedDurationFromSpecialOffer () {
      // If the special offer has a duration that is not in the default durations list, we add it so it can be selected
      if (!this.durations.some(duration => duration.value === this.dataRoomCustomPricing.months)) {
        const newCustomMonthsCount = Number(this.dataRoomCustomPricing.months)
        const newDurationValue = {
          value: newCustomMonthsCount,
          text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: newCustomMonthsCount }, newCustomMonthsCount),
        }

        const newDurations = [...this.durations, newDurationValue]
        this.durations = newDurations.sort((a, b) => a.value - b.value)
      }

      this.durations.forEach(duration => {
        if (duration.value === this.dataRoomCustomPricing.months) {
          this.selectedDuration = duration
        }
      })
    },
    forceSelectedDataFromSpecialOffer () {
      let sizeToSet = this.dataRoomCustomPricing.size
      let unitToSet = this.dataRoomCustomPricing.unit

      const isSizeDataInDefaultList = this.data.some(
        data => this.getSizeInMb(data.value, data.unit) === this.getSizeInMb(sizeToSet, unitToSet),
      )
      // if the special offer has a size that is not in the default data list, we add it so it can be selected
      if (!isSizeDataInDefaultList) {
        if (unitToSet === 'Gb' && sizeToSet < 1) {
          sizeToSet = sizeToSet * 1000
          unitToSet = 'Mb'
        }

        const localizedUnit = this.$t(`projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.${unitToSet}`);

        const newDataValue = {
          value: sizeToSet,
          unit: unitToSet,
          text: `${sizeToSet} ${localizedUnit}`,
        }

        const newData = [...this.data, newDataValue]
        this.data = newData.sort((a, b) => {
          const aSizeInMb = this.getSizeInMb(a.value, a.unit)
          const bSizeInMb = this.getSizeInMb(b.value, b.unit)

          return aSizeInMb - bSizeInMb
        })
      }

      this.data.forEach(data => {
        if (data.value === sizeToSet && data.unit === unitToSet) {
          this.selectedData = data
        }
      })
    },
    getSizeInMb (size, unit) {
      return unit === 'Gb' ? size * 1000 : size
    },
    onSelectedAdditionalDataChange () {
      this.selectedData = ADDITIONAL_DATA_VALUE
      this.onDataRoomSettingsChange()

    },
    areSameSizeData(data1, data2) {
      return data1.value === data2.value && data1.unit === data2.unit
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/typography-tokens";

.customDurationChip,
.customDataChip {
  &.v-chip--active {
    :deep(.v-input__slot input),
    :deep(.v-select__selection),
    :deep(.v-input__append-inner) {
      color: #fff;
    }
  }
}
:deep(.customDataSelect) {
  .v-input__control {
    min-height: 20px !important;
    margin-left: 4px;
    width: 80px;
    & .v-field__overlay {
      display: none;
    }
    & .v-field__input {
      padding-left: 0;
      padding-right: 0;
      opacity: 1;
    }

    & .v-field__append-inner {
      padding-left: 0;
      & .v-icon {
        opacity: 1 !important;
      }
    }
    & .v-field__field {
      padding: 0;
      background-color: transparent;
      & .v-select__selection--coma {
        text-overflow: unset;
      }
      input::placeholder {
        color: rgb(var(--v-theme-text-secondary));
        opacity: 1;
      }
    }
  }
}

.offer-details-container {
  background-color: rgb(var(--v-theme-surface-secondary));
  border: get-border();
}

.offer-choice-chip {
  @include text('body-md-regular');

  background-color: rgb(var(--v-theme-surface-primary));
  color: rgb(var(--v-theme-text-secondary));
  border: get-border('border-secondary');

  :deep(.v-icon) {
    color: rgb(var(--v-theme-icon-primary));
  }

  &.v-chip--disabled {
    opacity: 1 !important;
    background-color: rgb(var(--v-theme-surface-disabled));
    color: rgb(var(--v-theme-text-disabled));
    border: get-border('border-disabled');

    :deep(input::placeholder) {
      color: rgb(var(--v-theme-text-disabled)) !important;
    }

    :deep(.v-icon) {
      color: rgb(var(--v-theme-icon-disabled));
    }
  }

  &--selected {
    @include text('body-md-bold');

    background-color: rgb(var(--v-theme-surface-info-bold));
    color: rgb(var(--v-theme-text-inverted));

    :deep(.v-icon) {
      color: rgb(var(--v-theme-icon-inverted));
    }
  }

  :deep(.v-chip__overlay) {
    background-color: rgba(var(--v-theme-surface-hover));
    opacity: 0;
    transition: none !important;
  }

  &:hover :deep(.v-chip__overlay) {
    opacity: 1;
  }
}
</style>
